/**
 * The one map of keyboard shortcuts (docs/design-system/DESIGN-SYSTEM.md,
 * "Accessibility and keyboard"; pattern: docs/design-system/snippets/shortcuts.ts).
 *
 * Every key the shell answers is listed here, and the Help popover renders
 * this list, so a shortcut cannot exist without being shown, nor be shown
 * without existing. The command bar's Cmd/Ctrl+K and the traces page's own
 * shortcut help read their entries from here too.
 *
 * Keys are written as chords: `Mod` is ⌘ on a Mac and Ctrl elsewhere, joined
 * with `+` ("Mod+K", "Shift+Enter"). A shortcut whose `keys` hold more than one
 * chord is a sequence ("g" then "a"); `alternatives` are other chords that do
 * the same thing ("ArrowUp" or "ArrowDown").
 *
 * Framework-free on purpose: no React, no browser import, so a unit test, the
 * shell and any feature can read it.
 *
 * Spec: specs/design-system/ledger-shell.feature
 */

import { branding } from "~/config/branding";
import { surfaceEnabled } from "~/config/productSurfaces";

export type ShortcutScope = "global" | "lists" | "palette" | "traces";

export interface Shortcut {
  id: string;
  /** One chord, or a sequence of chords pressed one after the other. */
  keys: string[];
  /** Other chords that do the same thing. */
  alternatives?: string[];
  label: string;
  /** A second line in the help, for what the label cannot say. */
  detail?: string;
  scope: ShortcutScope;
  /** A heading inside the scope, where the scope has several. */
  group?: string;
  /** Still answers while focus is in a text field. */
  whenTyping?: boolean;
}

/** How each scope is headed in the Help popover, in this order. */
export const SHORTCUT_SCOPES: ReadonlyArray<{
  scope: ShortcutScope;
  title: string;
}> = [
  { scope: "global", title: "Everywhere" },
  { scope: "lists", title: "Lists and search" },
  { scope: "palette", title: "Command palette" },
  { scope: "traces", title: "Trace Explorer" },
];

/** The sections `g` then a letter jumps to. `path` is a project route or absolute. */
export const GO_TO_TARGETS = [
  { id: "go-home", letter: "h", label: "Home", path: "/[project]" },
  {
    id: "go-analytics",
    letter: "a",
    label: "Analytics",
    path: "/[project]/analytics",
  },
  { id: "go-traces", letter: "t", label: "Traces", path: "/[project]/traces" },
  { id: "go-tasks", letter: "k", label: "Tasks", path: "/[project]/tasks" },
  { id: "go-settings", letter: "s", label: "Settings", path: "/settings" },
] as const;

export type GoToTargetId = (typeof GO_TO_TARGETS)[number]["id"];

export const shortcuts: Shortcut[] = [
  // Everywhere
  {
    id: "palette",
    keys: ["Mod+K"],
    label: "Open the command palette",
    scope: "global",
    whenTyping: true,
  },
  {
    id: "help",
    keys: ["?"],
    label: "Show keyboard shortcuts",
    scope: "global",
  },
  {
    id: "escape",
    keys: ["Escape"],
    label: "Close the topmost layer",
    scope: "global",
    whenTyping: true,
  },
  ...GO_TO_TARGETS.map(
    (target): Shortcut => ({
      id: target.id,
      keys: ["g", target.letter],
      label: `Go to ${target.label}`,
      scope: "global",
    }),
  ),
  {
    id: "new",
    keys: ["n"],
    label: "Start the page's New action",
    detail: "On pages that create something",
    scope: "global",
  },

  // Lists and search
  {
    id: "focus-search",
    keys: ["/"],
    label: "Focus the search field",
    detail: "On pages with a list search",
    scope: "lists",
  },
  {
    id: "clear-search",
    keys: ["Escape"],
    label: "Clear the search field",
    detail: "While the search field has focus",
    scope: "lists",
    whenTyping: true,
  },

  // Command palette
  {
    id: "palette-move",
    keys: ["ArrowDown"],
    alternatives: ["ArrowUp"],
    label: "Move between results",
    scope: "palette",
    whenTyping: true,
  },
  {
    id: "palette-open",
    keys: ["Enter"],
    label: "Open the highlighted result",
    scope: "palette",
    whenTyping: true,
  },
  {
    id: "palette-new-tab",
    keys: ["Mod+Enter"],
    label: "Open it in a new tab",
    scope: "palette",
    whenTyping: true,
  },
  {
    id: "palette-copy-link",
    keys: ["Mod+L"],
    label: "Copy a link to it",
    scope: "palette",
    whenTyping: true,
  },
  ...(surfaceEnabled("langy")
    ? [
        {
          id: "palette-ask-langy",
          keys: ["Tab"],
          label: `Ask ${branding.assistantName} what you typed`,
          detail: `When ${branding.assistantName} is available to you`,
          scope: "palette",
          whenTyping: true,
        } satisfies Shortcut,
      ]
    : []),

  // Trace Explorer (handled by features/traces-v2/components/TracesPage)
  {
    id: "traces-sidebar",
    keys: ["["],
    label: "Toggle filter sidebar",
    scope: "traces",
    group: "Navigation",
  },
  {
    id: "traces-search",
    keys: ["/"],
    label: "Focus search",
    scope: "traces",
    group: "Navigation",
  },
  {
    id: "traces-find",
    keys: ["Mod+F"],
    label: "Find in loaded traces",
    scope: "traces",
    group: "Navigation",
  },
  {
    id: "traces-help",
    keys: ["?"],
    label: "Show this help",
    scope: "traces",
    group: "Navigation",
  },
  {
    id: "traces-ask",
    keys: ["Mod+I"],
    label: "Ask AI to build a query",
    detail: "Describe what you want in plain English",
    scope: "traces",
    group: "Search",
    whenTyping: true,
  },
  {
    id: "traces-facet-or",
    keys: ["Shift+Click"],
    label: "Add a facet to the query with OR instead of AND",
    detail: "Mod + click a facet does the same",
    scope: "traces",
    group: "Search",
  },
  {
    id: "traces-flip-operator",
    keys: ["Click"],
    label: "Click an operator in the query to flip it in place",
    detail: "On AND or OR",
    scope: "traces",
    group: "Search",
  },
  {
    id: "traces-density",
    keys: ["D"],
    label: "Toggle density",
    detail: "Compact or comfortable, saved on this device",
    scope: "traces",
    group: "View",
  },
  {
    id: "traces-configure-facets",
    keys: ["C"],
    label: "Configure which facets show",
    scope: "traces",
    group: "Filter sidebar",
  },
  {
    id: "traces-expand-all",
    keys: ["E"],
    alternatives: ["Shift+Enter"],
    label: "Expand or collapse all sections",
    scope: "traces",
    group: "Filter sidebar",
  },
  {
    id: "traces-clear-filters",
    keys: ["X"],
    label: "Clear all filters",
    scope: "traces",
    group: "Filter sidebar",
  },
  {
    id: "traces-reset-lens",
    keys: ["R"],
    label: "Reset to the current lens",
    scope: "traces",
    group: "Filter sidebar",
  },
  {
    id: "traces-move-sections",
    keys: ["Tab"],
    label: "Move between sections and rows",
    scope: "traces",
    group: "Filter sidebar",
  },
  {
    id: "traces-toggle-facet",
    keys: ["Space"],
    label: "Toggle a facet",
    detail: "When its row has focus",
    scope: "traces",
    group: "Filter sidebar",
  },
  {
    id: "traces-pick-up-section",
    keys: ["Space"],
    label: "Pick up or drop a section",
    detail: "When its drag handle has focus",
    scope: "traces",
    group: "Reorder sections",
  },
  {
    id: "traces-move-section",
    keys: ["ArrowUp"],
    alternatives: ["ArrowDown"],
    label: "Move the picked-up section",
    scope: "traces",
    group: "Reorder sections",
  },
  {
    id: "traces-cancel-reorder",
    keys: ["Escape"],
    label: "Cancel reorder",
    scope: "traces",
    group: "Reorder sections",
  },
];

export function shortcutById(id: string): Shortcut {
  const shortcut = shortcuts.find((candidate) => candidate.id === id);
  if (!shortcut) throw new Error(`No shortcut "${id}" in the shortcuts map`);
  return shortcut;
}

export function shortcutsInScope(scope: ShortcutScope): Shortcut[] {
  return shortcuts.filter((shortcut) => shortcut.scope === scope);
}

const KEY_NAMES: Record<string, string> = {
  Escape: "Esc",
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  Enter: "Enter",
  Space: "Space",
  Tab: "Tab",
  Shift: "Shift",
  Alt: "Alt",
  Click: "Click",
};

/**
 * The parts of one chord as a person reads them on their platform:
 * `"Mod+K"` is `["⌘", "K"]` on a Mac and `["Ctrl", "K"]` elsewhere.
 */
export function formatChord({
  chord,
  isMac,
}: {
  chord: string;
  isMac: boolean;
}): string[] {
  return splitChord(chord).map((part) => {
    if (part === "Mod") return isMac ? "⌘" : "Ctrl";
    if (part === "Alt" && isMac) return "⌥";
    const named = KEY_NAMES[part];
    if (named) return named;
    return part.length === 1 ? part.toUpperCase() : part;
  });
}

/**
 * One chord as a single short string, for a hint inside one Kbd cap: "⌘K" on
 * a Mac, "Ctrl+K" elsewhere.
 */
export function formatChordLabel({
  chord,
  isMac,
}: {
  chord: string;
  isMac: boolean;
}): string {
  return formatChord({ chord, isMac }).join(isMac ? "" : "+");
}

/** `"Mod+K"` → `["Mod", "K"]`; a lone `"+"` stays one part. */
function splitChord(chord: string): string[] {
  if (chord === "+") return ["+"];
  return chord.split("+").filter((part) => part !== "");
}

interface KeyLike {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

/**
 * Whether a key event is exactly this chord: the named modifiers held and no
 * other. Shift is not counted for a printable symbol ("?" needs Shift on most
 * layouts), and a single letter matches either case only without modifiers
 * held, so Caps Lock does not stop "g" from answering.
 */
export function matchesChord({
  event,
  chord,
  isMac,
}: {
  event: KeyLike;
  chord: string;
  isMac: boolean;
}): boolean {
  const parts = splitChord(chord);
  const key = parts[parts.length - 1];
  if (!key) return false;
  const modifiers = new Set(parts.slice(0, -1));

  const wantsMod = modifiers.has("Mod");
  const wantsCtrl = modifiers.has("Ctrl") || (wantsMod && !isMac);
  const wantsMeta = modifiers.has("Meta") || (wantsMod && isMac);
  const wantsAlt = modifiers.has("Alt");
  const wantsShift = modifiers.has("Shift");

  if (event.ctrlKey !== wantsCtrl) return false;
  if (event.metaKey !== wantsMeta) return false;
  if (event.altKey !== wantsAlt) return false;

  const isSymbol = key.length === 1 && !/[a-z0-9]/i.test(key);
  if (!isSymbol && event.shiftKey !== wantsShift) return false;

  if (key === "Space") return event.key === " ";
  if (key.length === 1) return event.key.toLowerCase() === key.toLowerCase();
  return event.key === key;
}
